/**
 * The segmented control of Screens `7c` — butted segments inside one hairline-bordered pill,
 * the active one lifted by a ghost fill rather than a colour. Used for Appearance and for the
 * English-translations picker.
 *
 * Hand-built rather than Mantine's `SegmentedControl` because 7c's version has no sliding
 * indicator, no per-segment radius and no gap: it is a row of labels in a box, and matching
 * that through Mantine's styles API would be more code than the box is.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  /** For screen readers — the visible label is the settings row's. */
  label: string;
}) {
  return (
    <span
      role="radiogroup"
      aria-label={label}
      style={{
        display: 'inline-flex',
        borderRadius: 7,
        border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            style={{
              padding: '3px 10px',
              fontSize: 11,
              fontFamily: 'inherit',
              border: 'none',
              cursor: 'pointer',
              color: active ? 'var(--nn-text-strong)' : 'var(--nn-muted)',
              background: active
                ? 'color-mix(in srgb, var(--nn-text) 9%, transparent)'
                : 'transparent',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </span>
  );
}
